import React, { useState, useMemo } from "react";
import { useUserGroups } from "../../hooks/useUserGroups";
import {
  Box,
  Button,
  Paper,
  Typography,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useQuery, useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  LIST_FORM_SUBMISSIONS,
  LIST_FORMS,
  APPROVE_SUBMISSION,
  DELETE_FORM_SUBMISSION,
  FormSubmission,
  Form,
} from "../../graphql/operations";
import { Helmet } from "react-helmet-async";
import PrintIcon from "@mui/icons-material/Print";

const FormSubmissionList: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin } = useUserGroups();
  const [filters, setFilters] = useState({
    formId: "",
    submittedBy: "",
    formType: "", // New filter for form type
  });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState("");

  // Mutation for approving submissions
  const [approveSubmission] = useMutation(APPROVE_SUBMISSION);
  const [deleteFormSubmission] = useMutation(DELETE_FORM_SUBMISSION);

  // Query for all forms (for the create dialog and form type filtering)
  const {
    data: formsData,
    loading: formsLoading,
    error: formsError,
  } = useQuery(LIST_FORMS, {
    fetchPolicy: "cache-first",
  });

  // Query for submissions
  const { data, loading, error, refetch } = useQuery(LIST_FORM_SUBMISSIONS, {
    variables: {
      filter: {
        formId: filters.formId || undefined,
        submittedBy: filters.submittedBy || undefined,
      },
    },
    fetchPolicy: "network-only",
  });

  // Extract forms for the create dialog
  const availableForms = useMemo(() => {
    return formsData?.listForms?.items || [];
  }, [formsData]);

  // Get form type mapping for submissions
  const formTypeMap = useMemo(() => {
    const map: { [formId: string]: string } = {};
    availableForms.forEach((form: Form) => {
      map[form.id] = form.formType;
    });
    return map;
  }, [availableForms]);

  // Filter submissions based on form type
  const filteredSubmissions = useMemo(() => {
    const submissions = data?.listFormSubmissions?.items || [];

    if (!filters.formType) return submissions;

    return submissions.filter((submission: FormSubmission) => {
      const formType = formTypeMap[submission.formId];
      return formType === filters.formType;
    });
  }, [data, filters.formType, formTypeMap]);

  // Derive filter options directly from current data
  const filterOptions = useMemo(() => {
    const submissions = filteredSubmissions;

    const formOptions = Array.from(
      new Map(
        submissions
          .filter((s: FormSubmission) => s.formId && s.formTitle)
          .map((s: FormSubmission) => [
            s.formId,
            {
              formId: s.formId,
              formTitle: s.formTitle,
              formType: formTypeMap[s.formId] || "UNKNOWN",
            },
          ])
      ).values()
    ).sort((a: any, b: any) => a.formTitle.localeCompare(b.formTitle));

    const submittedBys = Array.from(
      new Map(
        submissions
          .filter((s: FormSubmission) => s.userId && s.submittedBy)
          .map((s: FormSubmission) => [
            s.userId,
            { userId: s.userId, username: s.submittedBy },
          ])
      ).values()
    ).sort((a: any, b: any) => a.username.localeCompare(b.username));

    // Form type options
    const formTypeOptions = Array.from(
      new Map(
        submissions
          .filter((s: FormSubmission) => formTypeMap[s.formId])
          .map((s: FormSubmission) => [
            formTypeMap[s.formId],
            formTypeMap[s.formId],
          ])
      ).values()
    ).sort();

    return { formOptions, submittedBys, formTypeOptions };
  }, [filteredSubmissions, formTypeMap]);

  const handleDeleteSubmission = async (
    id: string,
    formId: string,
    formTitle: string
  ) => {
    console.log({ id }, { formId }, { formTitle });
    if (
      window.confirm(
        `Are you sure you want to delete the submission for "${formTitle}"?`
      )
    ) {
      try {
        await deleteFormSubmission({
          variables: {
            id: id,
            formId: formId,
          },
        });
        // Refetch the submissions list
        await refetch();
      } catch (error) {
        console.error("Error deleting submission:", error);
        // You could show an alert here
      }
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      formId: "",
      submittedBy: "",
      formType: "",
    });
  };

  const handleCreateSubmission = () => {
    if (selectedFormId) {
      navigate(`/dashboard/submissions/new/${selectedFormId}`);
    }
  };

  const handleEditSubmission = (submissionId: string) => {
    navigate(`/dashboard/submissions/edit/${submissionId}`);
  };

  // Add the print handler
  const handlePrintSubmission = (submission: FormSubmission) => {
    navigate(`/dashboard/submissions/print/${submission.submissionId}`);
  };

  const handleApproveSubmission = async (
    formId: string,
    submissionId: string,
    userId: string,
    submittedBy: string
  ) => {
    try {
      await approveSubmission({
        variables: {
          formId: formId,
          submissionId: submissionId,
          userId: userId,
          submittedBy: submittedBy,
        },
      });
      // Refetch the submissions list to show updated status
      await refetch();
    } catch (error: any) {
      console.error("Error approving submission:", error);
      <Alert severity="error" sx={{ m: 2 }}>
        Error approving submission: {error?.message}
      </Alert>;
    }
  };

  const hasActiveFilters =
    filters.formId || filters.submittedBy || filters.formType;

  // Get the currently selected form title for display
  const selectedFormTitle = useMemo(() => {
    if (!filters.formId) return "";
    const selectedForm: any = filterOptions.formOptions.find(
      (option: any) => option.formId === filters.formId
    );
    return selectedForm ? selectedForm.formTitle : "";
  }, [filters.formId, filterOptions.formOptions]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading form submissions: {error.message}
      </Alert>
    );
  }

  const submissions = filteredSubmissions;

  return (
    <Box>
      <Helmet>
        <title>Almond Tree Dashboard - Form Submissions</title>
      </Helmet>

      {/* Create Submission Button */}
      {isAdmin && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{ backgroundColor: "#919749" }}
          >
            Create Submission
          </Button>
        </Box>
      )}

      {/* Filters */}
      {isAdmin && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <FilterListIcon color="action" sx={{ mr: 1 }} />
            <Typography variant="h6">Filters</Typography>
            {hasActiveFilters && (
              <Button
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                sx={{ ml: 2 }}
                size="small"
              >
                Clear Filters
              </Button>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              alignItems: "flex-end",
            }}
          >
            <FormControl size="small" sx={{ minWidth: 250 }}>
              <InputLabel>Form Title</InputLabel>
              <Select
                value={filters.formId}
                label="Form Title"
                onChange={(e) => handleFilterChange("formId", e.target.value)}
                renderValue={(selected) => {
                  if (!selected) return "All Forms";
                  const selectedForm: any = filterOptions.formOptions.find(
                    (option: any) => option.formId === selected
                  );
                  return selectedForm ? selectedForm.formTitle : selected;
                }}
              >
                <MenuItem value="">All Forms</MenuItem>
                {filterOptions.formOptions.map((option: any) => (
                  <MenuItem key={option.formId} value={option.formId}>
                    {option.formTitle} ({option.formType})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Submitted By</InputLabel>
              <Select
                value={filters.submittedBy}
                label="Submitted By"
                onChange={(e) =>
                  handleFilterChange("submittedBy", e.target.value)
                }
              >
                <MenuItem value="">All Users</MenuItem>
                {filterOptions.submittedBys.map((option: any) => (
                  <MenuItem key={option.userId} value={option.userId}>
                    {option.username}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Form Type</InputLabel>
              <Select
                value={filters.formType}
                label="Form Type"
                onChange={(e) => handleFilterChange("formType", e.target.value)}
              >
                <MenuItem value="">All Types</MenuItem>
                {filterOptions.formTypeOptions.map((type: any) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              onClick={() => refetch()}
              sx={{ alignSelf: "flex-start", mb: 0.5 }}
            >
              Apply Filters
            </Button>
          </Box>

          {/* Show selected filter info */}
          {hasActiveFilters && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Active filters:{" "}
                {filters.formId && `Form: "${selectedFormTitle}"`}
                {filters.formId && filters.submittedBy && " • "}
                {filters.submittedBy && `User: "${filters.submittedBy}"`}
                {(filters.formId || filters.submittedBy) &&
                  filters.formType &&
                  " • "}
                {filters.formType && `Type: "${filters.formType}"`}
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Results Count */}
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        {submissions.length} submission(s) found
        {hasActiveFilters && " (filtered)"}
        {filterOptions.formOptions.length > 0 &&
          ` • ${filterOptions.formOptions.length} unique forms`}
        {filterOptions.submittedBys.length > 0 &&
          ` • ${filterOptions.submittedBys.length} unique users`}
      </Typography>

      {/* Submissions List */}
      <Paper>
        {submissions.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              {hasActiveFilters
                ? "No submissions match your filters"
                : "No submissions found"}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              {hasActiveFilters
                ? "Try adjusting your filters or clear them to see all submissions."
                : isAdmin
                ? "Form submissions will appear here once users start submitting forms."
                : "Form submissions will appear here once you start submitting forms."}
            </Typography>
            {hasActiveFilters && (
              <Button variant="outlined" onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </Box>
        ) : (
          <Stack spacing={2}>
            {submissions.map((submission: FormSubmission) => {
              const formType = formTypeMap[submission.formId] || "UNKNOWN";
              const isComplianceForm = formType === "COMPLIANCE";
              const canEdit = isAdmin && isComplianceForm;

              return (
                <Paper
                  key={submission.submissionId}
                  variant="outlined"
                  sx={{ p: 2 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 1,
                          gap: 1,
                        }}
                      >
                        <Typography variant="h6">
                          Submission: {submission.submissionId?.substring(0, 8)}
                          ...
                        </Typography>
                        <Chip
                          label={submission.status}
                          size="small"
                          color={
                            submission.status === "APPROVED"
                              ? "success"
                              : submission.status === "REJECTED"
                              ? "error"
                              : submission.status === "SUBMITTED"
                              ? "info"
                              : "default"
                          }
                          variant="outlined"
                        />
                        {isComplianceForm && (
                          <Chip
                            label="COMPLIANCE"
                            size="small"
                            color="warning"
                            variant="filled"
                          />
                        )}
                      </Box>

                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ mb: 2, flexWrap: "wrap" }}
                      >
                        {isAdmin && (
                          <Chip
                            label={submission.formTitle}
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              handleFilterChange("formId", submission.formId)
                            }
                            clickable
                          />
                        )}
                        {!isAdmin && (
                          <Chip
                            label={submission.formTitle}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        <Chip
                          label={`v${submission.formVersion}`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={formType}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                        {isAdmin && (
                          <Chip
                            label={`By: ${submission.submittedBy}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            onClick={() =>
                              handleFilterChange(
                                "submittedBy",
                                submission.userId
                              )
                            }
                            clickable
                          />
                        )}
                        {!isAdmin && (
                          <Chip
                            label={`By: ${submission.submittedBy}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )}
                      </Stack>

                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ mb: 2 }}
                      >
                        Submitted:{" "}
                        {new Date(submission.submittedAt).toLocaleString()}
                      </Typography>

                      {/* Display submission data */}
                      {submission.data && submission.data.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Submission Data:
                          </Typography>
                          <Stack spacing={1}>
                            {submission.data.map(
                              (field: any, index: number) => (
                                <Box
                                  key={index}
                                  sx={{ display: "flex", gap: 1 }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: "bold" }}
                                  >
                                    {field.label}:
                                  </Typography>
                                  <Typography variant="body2">
                                    {field.value}
                                  </Typography>
                                </Box>
                              )
                            )}
                          </Stack>
                        </Box>
                      )}

                      {/* Display metadata if available */}
                      {submission.metadata && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="textSecondary">
                            IP: {submission.metadata.ipAddress} • Device:{" "}
                            {submission.metadata.deviceType || "Unknown"}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Action Buttons for Admin */}
                    {isAdmin && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {/* Edit Button for Compliance Forms */}
                        {canEdit && (
                          <IconButton
                            color="primary"
                            onClick={() =>
                              handleEditSubmission(submission.submissionId)
                            }
                            title="Edit Compliance Submission"
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                        )}
                        {submission.status === "APPROVED" && (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            {/* Existing buttons... */}

                            <IconButton
                              onClick={() => handlePrintSubmission(submission)}
                              color="primary"
                              title="Print Submission"
                              size="small"
                            >
                              <PrintIcon />
                            </IconButton>
                          </Box>
                        )}
                        {/* Approve Button for SUBMITTED status */}
                        {submission.status !== "APPROVED" && (
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<CheckCircleIcon />}
                            onClick={() =>
                              handleApproveSubmission(
                                submission.formId,
                                submission.submissionId,
                                submission.userId,
                                submission.submittedBy
                              )
                            }
                            size="small"
                          >
                            Approve
                          </Button>
                        )}
                        {/* Status Chip for non-SUBMITTED submissions */}
                        {submission.status !== "SUBMITTED" && (
                          <Chip
                            label={submission.status}
                            color={
                              submission.status === "APPROVED"
                                ? "success"
                                : submission.status === "REJECTED"
                                ? "error"
                                : "default"
                            }
                            variant="filled"
                            size="small"
                          />
                        )}
                        <Tooltip title="Delete Submission">
                          <IconButton
                            onClick={() =>
                              handleDeleteSubmission(
                                submission.submissionId,
                                submission.formId,
                                submission.formTitle
                              )
                            }
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Paper>

      {/* Create Submission Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Submission</DialogTitle>
        <DialogContent>
          {formsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : formsError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              Error loading forms: {formsError.message}
            </Alert>
          ) : (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Select Form</InputLabel>
              <Select
                value={selectedFormId}
                label="Select Form"
                onChange={(e) => setSelectedFormId(e.target.value)}
              >
                <MenuItem value="">Choose a form...</MenuItem>
                {availableForms.map((form: Form) => (
                  <MenuItem key={form.id} value={form.id}>
                    {form.title} (v{form.version}) - {form.formType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateSubmission}
            variant="contained"
            disabled={!selectedFormId || formsLoading}
          >
            Create Submission
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FormSubmissionList;
